/**
 * Centralized React Query configuration with tiered caching strategy.
 *
 * ┌──────────┬────────────────────────────────────────────────────────────┐
 * │  Tier    │  Description                                             │
 * ├──────────┼────────────────────────────────────────────────────────────┤
 * │  AUTH    │  User session, profile, role — short cache, invalidated  │
 * │          │  on auth state change.  staleTime = 0                    │
 * ├──────────┼────────────────────────────────────────────────────────────┤
 * │  SEMI    │  Therapist lists, services, modalities, config, partner  │
 * │  STATIC  │  data — rarely changes.  staleTime = 5 min              │
 * ├──────────┼────────────────────────────────────────────────────────────┤
 * │  DYNAMIC │  Bookings, messages, availability, realtime data —      │
 * │          │  short cache + manual invalidation.  staleTime = 30 s   │
 * ├──────────┼────────────────────────────────────────────────────────────┤
 * │  ADMIN   │  Admin panel data — moderate cache.  staleTime = 2 min  │
 * └──────────┴────────────────────────────────────────────────────────────┘
 */

// ─── Cache durations ─────────────────────────────────────────────────────────
export const CACHE_TIME = {
  /** Auth-related: profile, role, session — no stale tolerance */
  AUTH: 0,
  /** Dynamic data: bookings, messages, availability */
  DYNAMIC: 30 * 1000,          // 30 seconds
  /** Admin panel data */
  ADMIN: 2 * 60 * 1000,        // 2 minutes
  /** Semi-static reference data: services, modalities, config */
  SEMI_STATIC: 5 * 60 * 1000,  // 5 minutes
  /** Static reference data: countries, diagnosis codes */
  STATIC: 15 * 60 * 1000,      // 15 minutes
} as const;

// ─── Query option presets ────────────────────────────────────────────────────
export const queryPresets = {
  /** Auth-related queries — always fresh, invalidate on auth change */
  auth: {
    staleTime: CACHE_TIME.AUTH,
    retry: 1,
    refetchOnWindowFocus: false,
  },
  /** Dynamic user-specific data (bookings, messages) */
  dynamic: {
    staleTime: CACHE_TIME.DYNAMIC,
    retry: 1,
    refetchOnWindowFocus: false,
  },
  /** Admin panel queries */
  admin: {
    staleTime: CACHE_TIME.ADMIN,
    retry: 1,
    refetchOnWindowFocus: false,
  },
  /** Semi-static reference data (services, modalities, practitioner lists) */
  semiStatic: {
    staleTime: CACHE_TIME.SEMI_STATIC,
    retry: 2,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 5000),
    refetchOnWindowFocus: false,
  },
  /** Static reference data (countries, diagnosis codes, healing stages) */
  static: {
    staleTime: CACHE_TIME.STATIC,
    retry: 2,
    refetchOnWindowFocus: false,
  },
} as const;

// ─── Query key factory ──────────────────────────────────────────────────────
// Ensures consistent key structure across the app.
export const queryKeys = {
  // Auth tier
  auth: {
    profile: (userId: string) => ['profile', userId] as const,
    clientProfile: (userId: string) => ['client-profile', userId] as const,
    practitionerProfile: (userId: string) => ['practitioner-profile', userId] as const,
    role: (userId: string) => ['user-role', userId] as const,
  },

  // Semi-static tier
  catalog: {
    services: () => ['services'] as const,
    featuredServices: () => ['featured-services'] as const,
    featuredPractitioners: () => ['featured-practitioners'] as const,
    therapyModalities: () => ['therapy-modality-stats'] as const,
    healingStages: () => ['healing-stages'] as const,
    effectiveDomains: () => ['effective-domains'] as const,
    subcategories: (domainKey: string) => ['service-subcategories', domainKey] as const,
    partners: () => ['partner-organizations'] as const,
    issueTags: () => ['issue_tags'] as const,
    certifications: () => ['certification-lists'] as const,
    countries: () => ['countries'] as const,
    diagnosisCodes: () => ['diagnosis-codes'] as const,
  },

  // Dynamic tier
  bookings: {
    all: (userId: string) => ['appointments', userId] as const,
    detail: (bookingId: string) => ['booking', bookingId] as const,
    history: (userId: string) => ['appointment-history', userId] as const,
  },
  messages: {
    conversation: (conversationId: string) => ['conversation', conversationId] as const,
    unread: (userId: string) => ['unread-messages', userId] as const,
  },
  dashboard: {
    practitionerClients: (userId: string) => ['client-roster', userId] as const,
    practitionerServices: (userId: string) => ['practitioner-services', userId] as const,
    clientSaved: (userId: string) => ['saved-practitioners', userId] as const,
  },

  // Admin tier
  admin: {
    practitioners: () => ['admin-practitioners'] as const,
    therapyModalities: () => ['admin-therapy-modalities'] as const,
    nutrients: () => ['admin-nutrient-deficiencies'] as const,
    campaigns: () => ['email-campaigns'] as const,
  },
} as const;

// ─── Auth-aware invalidation keys ───────────────────────────────────────────
// These query key prefixes will be invalidated when auth state changes.
export const AUTH_INVALIDATION_PREFIXES = [
  'profile',
  'client-profile',
  'practitioner-profile',
  'user-role',
  'appointments',
  'appointment-history',
  'unread-messages',
  'client-roster',
  'practitioner-services',
  'saved-practitioners',
  'follow-up-rules',
  'follow-up-logs',
] as const;
